import type { CharacterDef, InputState, ItemId, SurfaceKind, Vec2 } from '../core/types';
import { IDLE_INPUT } from '../core/types';
import type { Track } from './track';
import { clamp, damp, dot } from '../core/math';

const SURFACE: Record<SurfaceKind, { top: number; accel: number; drag: number; bump: number }> = {
  road: { top: 1.0, accel: 1.0, drag: 0.55, bump: 0 },
  kerb: { top: 0.985, accel: 0.97, drag: 0.7, bump: 1.4 },
  sidewalk: { top: 0.66, accel: 0.62, drag: 2.6, bump: 2.4 },
  boost: { top: 1.3, accel: 1.35, drag: 0.25, bump: 0 },
};

export interface DerivedStats {
  topSpeed: number;
  accel: number;
  turnRate: number;
  grip: number;
  mass: number;
}

const derive = (c: CharacterDef): DerivedStats => ({
  // a nehezebb autó ugyanazzal a motorral lassabb – teljesítmény/tömeg arány
  topSpeed: 330 + c.stats.topSpeed * 3 - (c.stats.weight - 5) * 1.2,
  accel: 400 + c.stats.accel * 35,
  turnRate: 3.0 + c.stats.handling * 0.06,
  grip: 2.2 + c.stats.grip * 0.3,
  mass: 700 + c.stats.weight * 40,
});

export interface KartOptions {
  id: number;
  character: CharacterDef;
  isPlayer: boolean;
  playerIndex: number;
  pos: Vec2;
  heading: number;
}

export interface KartSnapshot {
  id: number;
  characterId: string;
  pos: Vec2;
  heading: number;
  speed: number;
  lap: number;
  progress: number;
  item: ItemId | null;
}

/**
 * Egy versenyző fizikája. A világ hívja az update-et fix 1/60 lépésben,
 * utána oldja meg a kart-kart és fal ütközéseket.
 */
export class Kart {
  readonly id: number;
  readonly character: CharacterDef;
  readonly isPlayer: boolean;
  readonly playerIndex: number;
  readonly stats: DerivedStats;
  readonly radius: number;
  /** opcionális névcímke a kocsi felett (online: a játékos neve) */
  tag: string | null = null;

  pos: Vec2;
  vel: Vec2 = { x: 0, y: 0 };
  heading: number;
  angularVel = 0;

  input: InputState = { ...IDLE_INPUT };
  steer = 0;

  surface: SurfaceKind = 'road';
  onTrack = true;
  bump = 0;
  /** friss falütközés jelző (a FX ebből szór szikrát) */
  wallHit = 0;

  lap = 0;
  progress = 0;
  totalProgress = 0;
  /** az aktuális körben már átlépte-e a fél távot (kör-számlálás védelme) */
  lapCheckpoint = false;
  /** a rajtvonalon már áthaladt-e (a rács a vonal mögött indul) */
  startCrossed = false;
  lapStartTime = 0;
  lapTimes: number[] = [];
  bestLap: number | null = null;
  finished = false;
  finishTime = 0;

  driftActive = false;
  driftDir = 0;
  driftCharge = 0;
  driftLevel = 0;

  boostTimer = 0;
  boostStrength = 1;
  boostCooldown = 0;
  spinTimer = 0;
  spinDir = 1;
  slowTimer = 0;
  slowFactor = 1;
  /** jeges felület: hátralévő csúszás */
  slipTimer = 0;
  slipDir = 1;
  /** pálya-zóna (hóátfúvás / jég) újraaktiválásának hátralévő ideje */
  zoneCooldown = 0;
  /** kátyú utáni rövid immunitás, ne pördüljön újra ugyanabban */
  potholeCooldown = 0;
  shieldTimer = 0;
  hopTimer = 0;
  hopMax = 0.16;
  invulnTimer = 0;

  abilityCooldown = 0;
  abilityActive = 0;

  item: ItemId | null = null;
  itemRollTimer = 0;
  itemRollTotal = 0;
  itemRollTarget: ItemId | null = null;

  /** vizuális: pillanatnyi megcsúszás a haladási irány és az orr között */
  slipAngle = 0;
  /** vizuális: ugrás magassága 0..1 */
  hopPhase = 0;

  /** battle mód: csapat (0 = piros, 1 = kék), életek */
  battleMode = false;
  team = 0;
  lives = 3;
  out = false;
  /** respawn hátralévő ideje (mp), 0 = aktív */
  respawnTimer = 0;

  constructor(opts: KartOptions) {
    this.id = opts.id;
    this.character = opts.character;
    this.isPlayer = opts.isPlayer;
    this.playerIndex = opts.playerIndex;
    this.stats = derive(opts.character);
    this.radius = Math.max(opts.character.vehicle.width, opts.character.vehicle.length) * 0.36;
    this.pos = { ...opts.pos };
    this.heading = opts.heading;
  }

  get speed(): number {
    return dot(this.vel, { x: Math.cos(this.heading), y: Math.sin(this.heading) });
  }

  get speedKmh(): number {
    return Math.hypot(this.vel.x, this.vel.y) * 0.3;
  }

  get maxSpeedNow(): number {
    const s = SURFACE[this.surface];
    let top = this.stats.topSpeed * s.top;
    if (this.boostTimer > 0) top *= 1.28 * this.boostStrength;
    if (this.slowTimer > 0) top *= this.slowFactor;
    if (this.hopTimer > 0 && this.surface === 'sidewalk') top = this.stats.topSpeed;
    return top;
  }

  setInput(input: InputState): void {
    this.input = input;
  }

  applyBoost(duration: number, strength = 1): void {
    this.boostTimer = Math.max(this.boostTimer, duration);
    this.boostStrength = Math.max(this.boostStrength, strength);
    this.boostCooldown = 0;
  }

  applySlow(factor: number, duration: number): void {
    this.slowFactor = Math.min(this.slowFactor, factor);
    this.slowTimer = Math.max(this.slowTimer, duration);
  }

  spinOut(duration = 1.1): void {
    this.spinTimer = Math.max(this.spinTimer, duration);
    // determinisztikus pördülési irány (online/teszt szempontból fontos)
    this.spinDir = (Math.floor(Math.abs(this.pos.x) + Math.abs(this.pos.y)) & 1) === 0 ? -1 : 1;
    this.vel.x *= 0.55;
    this.vel.y *= 0.55;
  }

  /**
   * Jeges folt: nem pördül ki teljesen, de a tapadás pár tized mp-re elvész,
   * így az autó kifarol és tovább csúszik (mint egy jeges kanyarban).
   */
  slipOut(duration = 0.75): void {
    this.slipTimer = Math.max(this.slipTimer, duration);
    this.slipDir = (Math.floor(Math.abs(this.pos.x) + Math.abs(this.pos.y)) & 1) === 0 ? 1 : -1;
  }

  update(dt: number, track: Track): void {
    this.boostTimer = Math.max(0, this.boostTimer - dt);
    this.boostCooldown = Math.max(0, this.boostCooldown - dt);
    this.spinTimer = Math.max(0, this.spinTimer - dt);
    this.slipTimer = Math.max(0, this.slipTimer - dt);
    this.zoneCooldown = Math.max(0, this.zoneCooldown - dt);
    this.slowTimer = Math.max(0, this.slowTimer - dt);
    if (this.slowTimer === 0) this.slowFactor = 1;
    this.potholeCooldown = Math.max(0, this.potholeCooldown - dt);
    this.shieldTimer = Math.max(0, this.shieldTimer - dt);
    this.hopTimer = Math.max(0, this.hopTimer - dt);
    this.invulnTimer = Math.max(0, this.invulnTimer - dt);
    this.abilityCooldown = Math.max(0, this.abilityCooldown - dt);
    this.abilityActive = Math.max(0, this.abilityActive - dt);
    this.itemRollTimer = Math.max(0, this.itemRollTimer - dt);
    this.wallHit = Math.max(0, this.wallHit - dt);

    const info = track.surfaceAt(this.pos);
    this.surface = info.kind;
    this.onTrack = !info.outside;
    this.bump = SURFACE[info.kind].bump;

    const stunned = this.spinTimer > 0;

    const f = { x: Math.cos(this.heading), y: Math.sin(this.heading) };
    const n = { x: -f.y, y: f.x };

    let vF = dot(this.vel, f);
    let vL = dot(this.vel, n);

    const surface = SURFACE[this.surface];
    const top = this.maxSpeedNow;

    if (this.spinTimer > 0) {
      this.heading += this.spinDir * 8.5 * dt;
      vF *= Math.exp(-2.6 * dt);
      vL *= Math.exp(-2.6 * dt);
      this.steer = damp(this.steer, 0, 6, dt);
    } else if (!stunned) {
      const steerTarget = clamp(this.input.steer, -1, 1);
      this.steer = damp(this.steer, steerTarget, 11, dt);

      // gáz / fék
      const throttle = clamp(this.input.throttle, 0, 1);
      const brake = clamp(this.input.brake, 0, 1);
      if (throttle > 0) {
        const ratio = clamp(vF / Math.max(1, top), 0, 1);
        const force = this.stats.accel * surface.accel * throttle * (1 - Math.pow(ratio, 1.25));
        const boostExtra = this.boostTimer > 0 ? 240 : 0;
        vF += (force + boostExtra) * dt;
      }
      if (brake > 0) {
        vF -= 980 * brake * dt;
        if (vF < -top * 0.32) vF = -top * 0.32;
      }
      if (throttle < 0.02 && brake < 0.02) {
        vF *= Math.exp(-(0.9 + surface.drag) * dt);
      } else if (throttle > 0.02 && surface.drag > 1) {
        vF *= Math.exp(-surface.drag * 0.35 * dt);
      }

      // kormányzás
      const speedRatio = clamp(Math.abs(vF) / Math.max(1, top), 0, 1);
      const speedSteer = 1 - speedRatio * 0.48;
      const reverse = vF < -2 ? -1 : 1;

      const wantsDrift = this.input.drift && Math.abs(this.steer) > 0.22 && Math.abs(vF) > top * 0.42;
      if (wantsDrift && !this.driftActive && this.hopTimer <= 0) {
        this.driftActive = true;
        this.driftDir = Math.sign(this.steer);
        this.driftCharge = 0;
        this.driftLevel = 0;
        this.hopTimer = 0.16;
        this.hopMax = 0.16;
      }
      if (this.driftActive) {
        if (!this.input.drift || Math.abs(vF) < top * 0.25) {
          this.releaseDrift();
        } else {
          this.driftCharge += dt;
          this.driftLevel = this.driftCharge > 1.9 ? 2 : this.driftCharge > 0.85 ? 1 : 0;
          if (Math.sign(this.steer) === this.driftDir) this.driftDir = Math.sign(this.steer) || this.driftDir;
        }
      }

      const turnBoost = this.driftActive ? 1.38 : 1;
      const turn = this.stats.turnRate * speedSteer * turnBoost * reverse;
      this.heading += this.steer * turn * dt;

      // laterális tapadás
      const slipping = this.slipTimer > 0;
      const grip = this.stats.grip * (this.driftActive ? 0.3 : 1) * (slipping ? 0.14 : 1);
      const gripDecay = Math.exp(-grip * dt);
      if (this.driftActive) {
        // a kanyar felé "kifelé csúszik" az autó
        vL -= this.steer * Math.abs(vF) * 0.9 * dt;
      }
      if (slipping) {
        // jeges folt: az autó kifarol, de a sebessége nem törik meg
        this.heading += this.slipDir * 0.8 * dt;
      }
      vL *= gripDecay;
    } else {
      // hop: rövid ideig nincs tapadás-vesztés, de gázadás igen
      vL *= Math.exp(-2 * dt);
    }

    const bf = { x: Math.cos(this.heading), y: Math.sin(this.heading) };
    const bn = { x: -bf.y, y: bf.x };
    this.vel.x = bf.x * vF + bn.x * vL;
    this.vel.y = bf.y * vF + bn.y * vL;

    // sebességplafon – a boost-padról lecsúszva NEM vágjuk le egyből a sebességet,
    // hanem kb. 0,3 mp alatt engedjük vissza (különben „fékezésnek" érződik)
    const sp = Math.hypot(this.vel.x, this.vel.y);
    const cap = top * 1.06;
    if (sp > cap) {
      const excess = sp - cap;
      const softened = cap + excess * Math.exp(-1.8 * dt);
      const k = softened / sp;
      this.vel.x *= k;
      this.vel.y *= k;
    }

    this.pos.x += this.vel.x * dt;
    this.pos.y += this.vel.y * dt;

    this.resolveWalls(track, info);

    // vizuális megcsúszás
    const travel = Math.atan2(this.vel.y, this.vel.x);
    const slip = Math.hypot(this.vel.x, this.vel.y) > 20 ? wrapAngle(travel - this.heading) : 0;
    this.slipAngle = this.driftActive || Math.abs(slip) > 0.6 ? damp(this.slipAngle, slip, 8, dt) : damp(this.slipAngle, 0, 10, dt);
    this.hopPhase = this.hopTimer > 0 ? Math.sin((1 - this.hopTimer / Math.max(0.01, this.hopMax)) * Math.PI) : 0;

    this.progress = track.progressOfS(info.s - track.startS);
    this.totalProgress = this.lap + this.progress;
  }

  private releaseDrift(): void {
    if (this.driftCharge > 1.9) this.applyBoost(1.15, 1.25);
    else if (this.driftCharge > 0.85) this.applyBoost(0.7, 1.12);
    this.driftActive = false;
    this.driftCharge = 0;
    this.driftLevel = 0;
    this.driftDir = 0;
  }

  private resolveWalls(track: Track, info: ReturnType<Track['surfaceAt']>): void {
    if (!info.outside) return;
    const sample = track.samples[Math.floor(track.progressOfS(info.s) * track.samples.length) % track.samples.length];
    const side = Math.sign(info.lateral) || 1;
    const limit = track.corridorHalf - this.radius * 0.6;
    this.pos.x = sample.pos.x + sample.normal.x * side * limit;
    this.pos.y = sample.pos.y + sample.normal.y * side * limit;

    const nx = sample.normal.x * side;
    const ny = sample.normal.y * side;
    const into = this.vel.x * nx + this.vel.y * ny;
    if (into > 0) {
      this.vel.x -= nx * into * 1.4;
      this.vel.y -= ny * into * 1.4;
    }
    this.vel.x *= 0.72;
    this.vel.y *= 0.72;
    this.bump = 6;
    this.wallHit = 0.22;
  }

  /** az ugrás / képesség aktiválása */
  activateAbility(): boolean {
    if (this.abilityCooldown > 0) return false;
    const a = this.character.ability;
    this.abilityCooldown = a.cooldown;
    this.abilityActive = a.duration;
    if (a.id === 'hop') {
      this.hopTimer = a.duration;
      this.hopMax = a.duration;
    }
    return true;
  }
}

const wrapAngle = (a: number): number => {
  let x = a;
  while (x > Math.PI) x -= Math.PI * 2;
  while (x < -Math.PI) x += Math.PI * 2;
  return x;
};
